import { useContext, useEffect, useRef, useState } from 'react';
import { Box, Typography, Button, Modal, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import '../../styles/fonts/fonts.css';
import { Link, useNavigate } from 'react-router-dom';
import StyledButton from 'components/button/StyledButton';
import ReactMarkdown from 'react-markdown';
import { useGetEcomLatestQuery, useGetPrivacyPolicyQuery, useGetUserAgreementQuery, useLazyGetFirmsQuery } from '../../api/api';
import { ProjectContext } from "context/ProjectContext";
import FormSelect from 'components/form/FormSelect';

export type ModalType = 'deleteUser' | 'addFirm' | 'addFirmSuccess' | 'finalFirmAdminWarning' | 'addProject' | 'addProjectSuccess' | 
'updateProjectSuccess' | 'uploadProject' | 'deleteProject' | 'addEcomSuccess' | 'deleteEcom' | 'updateCarbonFactor' | 'deleteFirm' | 
'userAgreement' | 'privacyPolicy' | 'updateFirm' | '';

interface FigmaModalProps {
    open: boolean;
    onClose: any;
    values?: any;
    type: ModalType;
    onResponse?: (response: string) => void;
}

const FigmaModal: React.FC<FigmaModalProps> = ({ open, onClose, values = {}, type, onResponse }) => {
    const { data: userAgreement } = useGetUserAgreementQuery();
    const { data: privacyPolicy } = useGetPrivacyPolicyQuery()
    const { data: activeEcom } = useGetEcomLatestQuery();
    const [triggerGetFirms, { data: firms, isSuccess: firmsLoaded }] = useLazyGetFirmsQuery();

    const navigate = useNavigate();

    const fileInputRef = useRef<HTMLInputElement>(null);
    const firmSelectRef = useRef<HTMLSelectElement>(null);
    const [selectedValues, setSelectedValues] = useState<any>({})

    const context = useContext(ProjectContext);
    if (!context) {
        throw new Error('ProjectContext error');
    }
    const { setProjectId, projectId, setIsNewProject } = context;

    useEffect(() => {
        if (type === 'uploadProject') {
            triggerGetFirms();
        }
    }, [type, triggerGetFirms]);

    const modalSizes = {
        'addFirm': {
            'width': '885px',
            'height': '552px',
        },
        'addFirmSuccess': {
            'width': '885px',
            'height': '672px',
        },
        'finalFirmAdminWarning': {
            'width': '700px',
            'height': '500px',
        },
        'addProject': {
            'width': '885px',
            'height': '651px',
        },
        'addProjectSuccess': {
            'width': '650px',
            'height': '550px',
        },
        'updateProjectSuccess': {
            'width': '550px',
            'height': '450px',
        },
        'deleteProject': {
            'width': '550px',
            'height': '450px',
        },
        'uploadProject': {
            'width': '550px',
            'height': '380px',
        },
        'deleteFirm': {
            'width': '550px',
            'height': '480px',
        },
        'deleteEcom': {
            'width': '550px',
            'height': '480px',
        },
        'deleteUser': {
            'width': '550px',
            'height': '480px',
        },
        'addEcomSuccess': {
            'width': '885px',
            'height': '672px',
        },
        'updateCarbonFactor': {
            'width': '885px',
            'height': '672px',
        },
        'userAgreement': {
            'width': '885px',
            'height': '90%',
        },
        'privacyPolicy': {
            'width': '885px',
            'height': '90%',
        },
        'updateFirm': {
            'width': '885px',
            'height': '651px',
        },
    }

    const modalWidth = modalSizes[type]?.width ?? '885px';
    const modalHeight = modalSizes[type]?.height ?? '552px';

    const handleResponse = (response: any) => {
        if (onResponse) {
            onResponse(response);
        }
        onClose();
    };

    const renderContent = () => {
        switch (type) {
            case 'addFirm':
                return (
                    <>
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', marginBottom: '10px' }}>
                            <img src="/assets/icons/WarningCircle.svg" alt="Warning Icon" width={147} height={147} />
                        </div>
                        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                            <Typography
                                style={{
                                    color: '#1A1A1A',
                                    fontSize: 28,
                                    fontFamily: 'Helvetica Neue LT Std',
                                    fontWeight: 300,
                                    wordWrap: 'break-word',
                                }}
                            >
                                New Firm Creation Acknowledgement
                            </Typography>
                            <Typography
                                style={{
                                    color: '#1A1A1A',
                                    fontSize: 18,
                                    fontFamily: 'Helvetica Neue LT Std',
                                    fontWeight: '700',
                                    wordWrap: 'break-word',
                                    marginTop: "28px",
                                }}
                            >
                                Before you create a new firm, please confirm that:
                            </Typography>
                            <Typography
                                style={{
                                    color: '#1A1A1A',
                                    fontSize: 18,
                                    fontFamily: 'Helvetica Neue LT Std',
                                    fontWeight: '700',
                                    wordWrap: 'break-word',
                                }}
                            >
                                You have checked that this Firm does not already exist in the Database
                            </Typography>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '60px', marginBottom: '80px' }}>
                            <Button variant="outlined" style={{ marginRight: '10px' }} onClick={() => onClose()}>Cancel</Button>
                            <Button variant="contained" color="primary" component={Link} to="/add-firm">I Confirm</Button>
                        </div>
                    </>
                );
            case 'addFirmSuccess':
                return (
                    <>
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', marginBottom: '10px' }}>
                            <img src="/assets/icons/CheckCircle.svg" alt="Check Icon" width={147} height={147} />
                        </div>
                        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                            <Typography
                                style={{
                                    color: '#1A1A1A',
                                    fontSize: 28,
                                    fontFamily: 'Helvetica Neue LT Std',
                                    fontWeight: 300,
                                    wordWrap: 'break-word',
                                }}
                            >
                                Firm Successfully Added!
                            </Typography>
                            <Typography
                                style={{
                                    color: '#1A1A1A',
                                    fontSize: 18,
                                    fontFamily: 'Helvetica Neue LT Std',
                                    fontWeight: '700',
                                    wordWrap: 'break-word',
                                    marginTop: "28px",
                                    maxWidth: "551px",
                                    maxHeight: "97px",
                                }}
                            >
                                The Firm{values.firmName ? `, ${values.firmName}, ` : ''}has successfully been created in the SE 2050 Database with {values.firmAdmin ? `${values.firmAdmin}` : 'admin_name'} as the Firm Admin. Please request {values.firmAdmin ? `${values.firmAdmin}` : 'admin_name'} creates an account to begin approving users. If you wish to review or edit this firm in the future you can do so from the Firms page.
                            </Typography>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '60px', marginBottom: '80px' }}>
                            <StyledButton label='Done' type='filled' onClick={onClose}></StyledButton>
                        </div>
                    </>
                );
            case 'finalFirmAdminWarning':
                return (
                    <>
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', marginBottom: '10px' }}>
                            <img src="/assets/icons/WarningCircle.svg" alt="Warning Icon" width={147} height={147} />
                        </div>
                        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                            <Typography
                                style={{
                                    color: '#1A1A1A',
                                    fontSize: 28,
                                    fontFamily: 'Helvetica Neue LT Std',
                                    fontWeight: 300,
                                    wordWrap: 'break-word',
                                }}
                            >
                                Cannot Delete or Alter Final Firm Admin
                            </Typography>
                            <Typography
                                style={{
                                    color: '#1A1A1A',
                                    fontSize: 18,
                                    fontFamily: 'Helvetica Neue LT Std',
                                    fontWeight: '700',
                                    wordWrap: 'break-word',
                                    marginTop: "28px",
                                }}
                            >
                                You can not delete or demote the last admin for this firm
                            </Typography>
                            <Typography
                                style={{
                                    color: '#1A1A1A',
                                    fontSize: 18,
                                    fontFamily: 'Helvetica Neue LT Std',
                                    fontWeight: '700',
                                    wordWrap: 'break-word',
                                }}
                            >
                                Assign a new firm admin first in order to make this change
                            </Typography>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '15px', marginBottom: '20px' }}>
                            <Button variant="contained" color="primary" onClick={onClose}>I Understand</Button>
                        </div>
                    </>
                );
            case 'addProject':
                return (
                    <>
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', marginBottom: '10px', marginTop: '50px' }}>
                            <img src="/assets/icons/WarningCircle.svg" alt="Warning Icon" width={147} height={147} />
                        </div>
                        <div style={{ textAlign: 'center', marginBottom: '20px', maxWidth: '621px' }}>
                            <Typography
                                style={{
                                    color: '#1A1A1A',
                                    fontSize: 28,
                                    fontFamily: 'Helvetica Neue LT Std',
                                    fontWeight: 300,
                                    wordWrap: 'break-word',
                                }}
                            >
                                New Project Creation Acknowledgement
                            </Typography>
                        </div>
                        <div style={{ textAlign: 'left', marginBottom: '20px', maxWidth: '621px' }}>
                            <Typography
                                style={{
                                    color: '#1A1A1A',
                                    fontSize: 18,
                                    fontFamily: 'Helvetica Neue LT Std',
                                    fontWeight: '600',
                                    wordWrap: 'break-word',
                                    marginTop: "28px",
                                    marginBottom: '10px',
                                }}
                            >
                                Before you create a new project, please confirm that:
                            </Typography>
                            <Typography
                                style={{
                                    color: '#1A1A1A',
                                    fontSize: 18,
                                    fontFamily: 'Helvetica Neue LT Std',
                                    fontWeight: '400',
                                    wordWrap: 'break-word',
                                    marginBottom: '10px',
                                }}
                            >
                                1. You have checked that this project does not already exist in your Firm's project list.
                            </Typography>
                            <Typography
                                style={{
                                    color: '#1A1A1A',
                                    fontSize: 18,
                                    fontFamily: 'Helvetica Neue LT Std',
                                    fontWeight: '400',
                                    wordWrap: 'break-word',
                                    marginBottom: '10px',
                                }}
                            >
                                2. You have collected structural material quantities following the guidance posted here, and you are ready to report.
                            </Typography>
                            <Typography
                                style={{
                                    color: '#1A1A1A',
                                    fontSize: 18,
                                    fontFamily: 'Helvetica Neue LT Std',
                                    fontWeight: '400',
                                    wordWrap: 'break-word',
                                }}
                            >
                                3. If you are planning on reporting embodied carbon results from an external tool, you have collected those results and are ready to report.
                            </Typography>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '60px', marginBottom: '80px' }}>
                            <Button variant="outlined" style={{ marginRight: '10px' }} onClick={() => onClose()}>Cancel</Button>
                            <Button variant="contained" color="primary" onClick={() => {
                                setProjectId(-1);
                                setIsNewProject(true);
                                navigate(`/add-project/1`);
                            }}>I Confirm</Button>
                        </div>
                    </>
                );
            case 'deleteProject':
                return (
                    <>
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', marginBottom: '10px', marginTop: '50px' }}>
                            <img src="/assets/icons/WarningCircle.svg" alt="Warning Icon" width={147} height={147} />
                        </div>
                        <Typography
                            style={{
                                color: '#1A1A1A',
                                fontSize: 28,
                                fontFamily: 'Helvetica Neue LT Std',
                                fontWeight: 300,
                                wordWrap: 'break-word',
                            }}
                        >
                            Confirm Project Deletion
                        </Typography>
                        <div style={{ textAlign: 'left', marginBottom: '20px', maxWidth: '621px' }}>
                            <Typography
                                style={{
                                    color: '#1A1A1A',
                                    fontSize: 18,
                                    fontFamily: 'Helvetica Neue LT Std',
                                    fontWeight: '600',
                                    wordWrap: 'break-word',
                                    marginTop: "28px",
                                    marginBottom: '10px',
                                }}
                            >
                                {`Are you sure you wish to delete ${values.name}?`}
                            </Typography>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px', marginBottom: '80px', gap: 10 }}>
                            <Button variant="contained" color="error" onClick={() => handleResponse('Confirm')}>Yes, delete this project</Button>
                            <Button variant="contained" style={{ marginRight: '10px' }} onClick={() => handleResponse('Cancel')}>Cancel</Button>
                        </div>
                    </>
                );
            case 'uploadProject': {
                const handleUpload = () => {
                    const file = fileInputRef.current?.files?.[0];
                    if (file && selectedValues) {
                        handleResponse({ file: fileInputRef.current?.files?.[0], firm: selectedValues?.firm });
                    }
                };

                return (
                    <>
                        <Typography
                            style={{
                                color: '#1A1A1A',
                                fontSize: 28,
                                fontFamily: 'Helvetica Neue LT Std',
                                fontWeight: 300,
                                wordWrap: 'break-word',
                            }}
                        >
                            Select Firm For Project Upload
                        </Typography>
                        <div style={{ textAlign: 'left', marginBottom: '5px', maxWidth: '621px' }}>
                            <Typography
                                style={{
                                    color: '#1A1A1A',
                                    fontSize: 18,
                                    fontFamily: 'Helvetica Neue LT Std',
                                    fontWeight: '600',
                                    wordWrap: 'break-word',
                                    marginTop: "28px",
                                    marginBottom: '10px',
                                }}
                            >
                                Please select the firm which applies to the projects you will be importing
                            </Typography>
                        </div>
                        <div style={{ width: '100%' }}>
                            <FormSelect
                                label="Select Firm"
                                menuItems={firms?.map(firm => firm.name) ?? []}
                                onChange={(e) => setSelectedValues({ file: selectedValues?.file, firm: e.target.value })}
                                value={selectedValues?.firm} error={undefined} helperText={undefined} disabled={undefined} name={undefined} />
                        </div>
                        <Box sx={{ mt: 1 }}>
                            <input
                                type="file"
                                accept=".xlsm, .xlsx"
                                ref={fileInputRef}
                                style={{ display: 'none' }}
                                onChange={(event) => setSelectedValues({ firm: selectedValues?.firm, file: event.target.value })}
                            />
                            <Button
                                variant="outlined"
                                onClick={() => fileInputRef.current?.click()}
                                fullWidth
                            >
                                {fileInputRef.current?.files?.[0]?.name ? 'File Uploaded' : 'Choose File'}
                            </Button>
                        </Box>
                        <span style={{ color: 'black', fontSize: 'small', height: 10 }}>{fileInputRef.current?.files?.[0]?.name}</span>

                        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px', marginBottom: '10px', gap: 10 }}>
                            <Button
                                variant="contained"
                                onClick={handleUpload}
                            >
                                Upload Project
                            </Button>
                            <Button
                                variant="contained"
                                style={{ marginRight: '10px' }}
                                onClick={() => handleResponse('Cancel')}
                            >
                                Cancel
                            </Button>
                        </div>
                    </>
                );
            }
            case 'addProjectSuccess':
                return (
                    <>
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', marginBottom: '10px' }}>
                            <img src="/assets/icons/CheckCircle.svg" alt="Check Icon" width={147} height={147} />
                        </div>
                        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                            <Typography
                                style={{
                                    color: '#1A1A1A',
                                    fontSize: 28,
                                    fontFamily: 'Helvetica Neue LT Std',
                                    fontWeight: 300,
                                    wordWrap: 'break-word',
                                }}
                            >
                                Project Successfully Added!
                            </Typography>
                            <Typography
                                style={{
                                    color: '#1A1A1A',
                                    fontSize: 18,
                                    fontFamily: 'Helvetica Neue LT Std',
                                    fontWeight: '700',
                                    wordWrap: 'break-word',
                                    marginTop: "28px",
                                    maxWidth: "551px",
                                    maxHeight: "97px",
                                }}
                            >
                                Your Project has successfully been created in the SE 2050 Database. If you wish to review or edit this project in the future you can do so from the Projects page.
                            </Typography>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '30px', marginBottom: '20px' }}>
                            <StyledButton label='Done' type='filled' onClick={() => {
                                setIsNewProject(false);
                                onClose();
                            }}></StyledButton>
                        </div>
                    </>
                );
            case 'updateProjectSuccess':
                return (
                    <>
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', marginBottom: '10px' }}>
                            <img src="/assets/icons/CheckCircle.svg" alt="Check Icon" width={147} height={147} />
                        </div>
                        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                            <Typography
                                style={{
                                    color: '#1A1A1A',
                                    fontSize: 28,
                                    fontFamily: 'Helvetica Neue LT Std',
                                    fontWeight: 300,
                                    wordWrap: 'break-word',
                                }}
                            >
                                Project Successfully Updated!
                            </Typography>
                            <Typography
                                style={{
                                    color: '#1A1A1A',
                                    fontSize: 18,
                                    fontFamily: 'Helvetica Neue LT Std',
                                    fontWeight: '700',
                                    wordWrap: 'break-word',
                                    marginTop: "28px",
                                    maxWidth: "551px",
                                    maxHeight: "97px",
                                }}
                            >
                                Your Project has successfully been updated.
                            </Typography>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px', marginBottom: '20px' }}>
                            <StyledButton label='Done' type='filled' onClick={() => {
                                setIsNewProject(false);
                                onClose();
                            }}></StyledButton>
                        </div>
                    </>
                );
            case 'addEcomSuccess':
                return (
                    <>
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', marginBottom: '10px' }}>
                            <img src="/assets/icons/CheckCircle.svg" alt="Check Icon" width={147} height={147} />
                        </div>
                        <div style={{
                            textAlign: 'center',
                            marginBottom: '20px',
                        }}>
                            <Typography
                                style={{
                                    color: '#1A1A1A',
                                    fontSize: 28,
                                    fontFamily: 'Helvetica Neue LT Std',
                                    fontWeight: 300,
                                    wordWrap: 'break-word',
                                }}
                            >
                                New ECOM Factors Version Successfully Added!
                            </Typography>
                            <Typography
                                style={{
                                    color: '#1A1A1A',
                                    fontSize: 18,
                                    fontFamily: 'Helvetica Neue LT Std',
                                    fontWeight: '700',
                                    wordWrap: 'break-word',
                                    marginTop: "28px",
                                    maxWidth: "443px",
                                    maxHeight: "97px",
                                    marginLeft: '15%',
                                }}
                            >
                                ECOM Factors have successfully been created in the SE 2050 Database. If you wish to review or edit this version in the future you can do so from the ECOM page.
                            </Typography>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '60px', marginBottom: '80px' }}>
                            <StyledButton label='Done' type='filled' onClick={onClose}></StyledButton>
                        </div>
                    </>
                );
            case 'updateCarbonFactor':
                return (
                    <>
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', marginBottom: '10px', marginTop: '50px' }}>
                            <img src="/assets/icons/WarningCircle.svg" alt="Warning Icon" width={147} height={147} />
                        </div>
                        <div style={{ textAlign: 'center', marginBottom: '20px', maxWidth: '621px' }}>
                            <Typography
                                style={{
                                    color: '#1A1A1A',
                                    fontSize: 28,
                                    fontFamily: 'Helvetica Neue LT Std',
                                    fontWeight: 300,
                                    wordWrap: 'break-word',
                                }}
                            >
                                ECOM Value Override
                            </Typography>
                        </div>
                        <div style={{ textAlign: 'left', marginBottom: '20px', maxWidth: '621px' }}>
                            <Typography
                                style={{
                                    color: '#1A1A1A',
                                    fontSize: 18,
                                    fontFamily: 'Helvetica Neue LT Std',
                                    fontWeight: '600',
                                    wordWrap: 'break-word',
                                    marginTop: "28px",
                                    marginBottom: '10px',
                                }}
                            >
                                By confirming you will be overriding the active ECOM Version {activeEcom?.['version'] ?? 'ECOM v1.1'} A1-A3 carbon factor for this material quantity. Do you wish to proceed?
                            </Typography>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '60px', marginBottom: '80px' }}>
                            <Button variant="outlined" style={{ marginRight: '10px' }} onClick={() => handleResponse('Cancel')}>Cancel</Button>
                            <Button variant="contained" color="primary" onClick={() => handleResponse('Confirm')}>I Confirm</Button>
                        </div>
                    </>
                );
            case 'deleteFirm':
                return (
                    <>
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', marginBottom: '10px', marginTop: '50px' }}>
                            <img src="/assets/icons/WarningCircle.svg" alt="Warning Icon" width={147} height={147} />
                        </div>
                        <Typography
                            style={{
                                color: '#1A1A1A',
                                fontSize: 28,
                                fontFamily: 'Helvetica Neue LT Std',
                                fontWeight: 300,
                                wordWrap: 'break-word',
                            }}
                        >
                            Confirm Firm Deletion
                        </Typography>
                        <div style={{ textAlign: 'center', marginBottom: '20px', maxWidth: '621px' }}>
                            <Typography
                                style={{
                                    color: '#1A1A1A',
                                    fontSize: 18,
                                    fontFamily: 'Helvetica Neue LT Std',
                                    fontWeight: '600',
                                    wordWrap: 'break-word',
                                    marginTop: "28px",
                                    marginBottom: '10px',
                                }}
                            >
                                Are you sure you wish to delete this firm? This action cannot be undone.
                            </Typography>
                            <Typography
                                style={{
                                    color: '#1A1A1A',
                                    fontSize: 16,
                                    fontFamily: 'Helvetica Neue LT Std',
                                    fontWeight: '400',
                                    wordWrap: 'break-word',
                                    marginTop: '20px',
                                }}
                            >
                                Deleting this firm will also remove all associated projects and user accounts.
                            </Typography>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '80px', gap: 10 }}>
                            <Button variant="contained" color="error" onClick={() => handleResponse('Confirm')}>Yes, delete this firm</Button>
                            <Button variant="contained" style={{ marginRight: '10px' }} onClick={() => handleResponse('Cancel')}>Cancel</Button>
                        </div>
                    </>
                );
            case 'deleteEcom':
                return (
                    <>
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', marginBottom: '10px', marginTop: '50px' }}>
                            <img src="/assets/icons/WarningCircle.svg" alt="Warning Icon" width={147} height={147} />
                        </div>
                        <Typography
                            style={{
                                color: '#1A1A1A',
                                fontSize: 28,
                                fontFamily: 'Helvetica Neue LT Std',
                                fontWeight: 300,
                                wordWrap: 'break-word',
                            }}
                        >
                            Confirm ECOM Version Deletion
                        </Typography>
                        <div style={{ textAlign: 'center', marginBottom: '20px', maxWidth: '621px' }}>
                            <Typography
                                style={{
                                    color: '#1A1A1A',
                                    fontSize: 18,
                                    fontFamily: 'Helvetica Neue LT Std',
                                    fontWeight: '600',
                                    wordWrap: 'break-word',
                                    marginTop: "28px",
                                    marginBottom: '10px',
                                }}
                            >
                                Are you sure you wish to delete this ECOM version? This action cannot be undone.
                            </Typography>
                            <Typography
                                style={{
                                    color: '#1A1A1A',
                                    fontSize: 16,
                                    fontFamily: 'Helvetica Neue LT Std',
                                    fontWeight: '400',
                                    wordWrap: 'break-word',
                                    marginTop: '20px',
                                }}
                            >
                                Deleting this ECOM version will remove all associated carbon factors and may affect project calculations.
                            </Typography>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '80px', gap: 10 }}>
                            <Button variant="contained" color="error" onClick={() => handleResponse('Confirm')}>Yes, delete this version</Button>
                            <Button variant="contained" style={{ marginRight: '10px' }} onClick={() => handleResponse('Cancel')}>Cancel</Button>
                        </div>
                    </>
                );
            case 'deleteUser':
                return (
                    <>
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', marginBottom: '10px', marginTop: '50px' }}>
                            <img src="/assets/icons/WarningCircle.svg" alt="Warning Icon" width={147} height={147} />
                        </div>
                        <Typography
                            style={{
                                color: '#1A1A1A',
                                fontSize: 28,
                                fontFamily: 'Helvetica Neue LT Std',
                                fontWeight: 300,
                                wordWrap: 'break-word',
                            }}
                        >
                            Confirm User Deletion
                        </Typography>
                        <div style={{ textAlign: 'center', marginBottom: '20px', maxWidth: '621px' }}>
                            <Typography
                                style={{
                                    color: '#1A1A1A',
                                    fontSize: 18,
                                    fontFamily: 'Helvetica Neue LT Std',
                                    fontWeight: '600',
                                    wordWrap: 'break-word',
                                    marginTop: "28px",
                                    marginBottom: '10px',
                                }}
                            >
                                Are you sure you wish to delete this user? This action cannot be undone.
                            </Typography>
                            <Typography
                                style={{
                                    color: '#1A1A1A',
                                    fontSize: 16,
                                    fontFamily: 'Helvetica Neue LT Std',
                                    fontWeight: '400',
                                    wordWrap: 'break-word',
                                    marginTop: '20px',
                                }}
                            >
                                This will remove the user's account and access to the system. Their project associations will be maintained.
                            </Typography>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '80px', gap: 10 }}>
                            <Button variant="contained" color="error" onClick={() => handleResponse('Confirm')}>Yes, delete this user</Button>
                            <Button variant="contained" style={{ marginRight: '10px' }} onClick={() => handleResponse('Cancel')}>Cancel</Button>
                        </div>
                    </>
                );
            case 'userAgreement':
                return (
                    <>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            height: '95%',
                        }}>
                            <Typography sx={{
                                color: '#1A1A1A',
                                fontSize: '32px',
                                fontFamily: 'Helvetica Neue LT Std',
                                fontWeight: 300,
                            }}>
                                User Agreement - SEI SE 2050
                            </Typography>
                            <Typography sx={{
                                color: '#1A1A1A',
                                fontSize: '16px',
                                fontFamily: 'Helvetica Neue LT Std',
                                fontWeight: 300,
                                minWidth: '90%',
                                maxWidth: '90%',
                                maxHeight: '80%',
                                paddingLeft: '10%',
                                paddingTop: '20px',
                                overflow: 'auto',
                            }}>
                                <ReactMarkdown>{userAgreement?.text ?? 'Loading...'}</ReactMarkdown>
                            </Typography>
                            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '60px', marginBottom: '80px' }}>
                                <StyledButton label='Close' type='filled' onClick={onClose}></StyledButton>
                            </div>
                        </Box>
                    </>
                );
            case 'privacyPolicy':
                return (
                    <>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            height: '95%',
                        }}>
                            <Typography sx={{
                                color: '#1A1A1A',
                                fontSize: '32px',
                                fontFamily: 'Helvetica Neue LT Std',
                                fontWeight: 300,
                            }}>
                                Privacy Policy - SEI SE 2050
                            </Typography>
                            <Typography sx={{
                                color: '#1A1A1A',
                                fontSize: '16px',
                                fontFamily: 'Helvetica Neue LT Std',
                                fontWeight: 300,
                                minWidth: '90%',
                                maxWidth: '90%',
                                maxHeight: '80%',
                                paddingLeft: '10%',
                                paddingTop: '20px',
                                overflow: 'auto',
                            }}>
                                <ReactMarkdown>{privacyPolicy?.text ?? 'Loading...'}</ReactMarkdown>
                            </Typography>
                            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '60px', marginBottom: '80px' }}>
                                <StyledButton label='Close' type='filled' onClick={onClose}></StyledButton>
                            </div>
                        </Box>
                    </>
                );
            case 'updateFirm':
                return (
                    <>
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', marginBottom: '10px' }}>
                            <img src="/assets/icons/CheckCircle.svg" alt="Check Icon" width={147} height={147} />
                        </div>
                        <div
                            style={{
                                textAlign: 'center',
                                marginBottom: '20px',
                                fontFamily: 'Helvetica Neue LT Std',
                            }}
                        >
                            <Typography
                                style={{
                                    color: '#1A1A1A',
                                    fontSize: 28,
                                    fontWeight: 300,
                                    wordWrap: 'break-word',
                                }}
                            >
                                Firm Successfully Updated!
                            </Typography>
                            <Typography
                                style={{
                                    color: '#1A1A1A',
                                    fontSize: 18,
                                    fontWeight: 700,
                                    wordWrap: 'break-word',
                                    marginTop: '28px',
                                    maxWidth: '551px',
                                    maxHeight: '97px',
                                }}
                            >
                                Your Firm has successfully been updated.
                            </Typography>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px', marginBottom: '20px' }}>
                            <StyledButton label="Done" type="filled" onClick={onClose}></StyledButton>
                        </div>
                    </>
                );
            default:
                return null;
        }
    };

    return (
        <Modal
            open={open}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
                    onClose();
                }
            }}
        >
            <div style={{
                width: modalWidth,
                height: modalHeight,
                background: 'white',
                boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
                borderRadius: 5,
                padding: '20px',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                margin: 'auto',
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
            }}>
                {renderContent()}
            </div>
        </Modal>
    );
};

export default FigmaModal;
